// vg-binding: P18.D-21   (the vendor portal page `/settings/integration`: three
//                         stacked cards — API Keys / Webhook / Delivery Log —
//                         built from @printway/ui primitives, 40px form
//                         controls, border-recolour focus, all copy through the
//                         `integration` i18n namespace in three locales.)
// vg-binding: P18.D-22   (the four UX rules that are not decoration: empty
//                         state per ROLE on all three cards; INLINE handshake
//                         cause + retry; a show-once dialog that cannot be
//                         dismissed by accident; 30s refetch + freshness badge
//                         so the card never claims `verified` for an endpoint
//                         the server already auto-disabled.)
//
// apps/vendor/src/pages/IntegrationSettingsPage.tsx
//
// Phase 18 Task 33 — the surface a vendor actually uses to run the Vendor API.
//
// ===========================================================================
// THE API HANDS US KEYS, NOT SENTENCES
// ===========================================================================
// task-29 deliberately ships NO `error.user_message` and no success `message`
// on the management lane: the API cannot see the caller's locale, and an
// English literal would LOOK localized and win the FE message-priority chain.
// Every error instead carries `details.i18n_key = "integration.errors.<CODE>"`
// plus a frozen English `error.message` meant for logs.
//
// So this file owns the other half of that contract: `errors.<CODE>` exists in
// en/vi/fr for EVERY code the lane can emit — the whole
// `VendorManagementErrorCode` union (the 17 machine-lane codes plus
// AUTH_CSRF_MISSING / ALREADY_REVOKED / KEY_LIMIT_REACHED /
// ROTATION_IN_PROGRESS), not just the ones the happy path meets. A missing key
// renders as the raw key string at a vendor, which is worse than English.
//
// Message priority (INTERFACE-STANDARDS § Frontend, reconciled with the repo
// i18n rule):
//   1. `error.user_message` — a localized override, if the server ever sets one
//   2. `t('errors.<CODE>')` — OUR catalog; this is the "localized copy exists"
//      branch the standard defers to
//   3. `error.message`      — the frozen English default (better than nothing)
//   4. network fallback
// HTTP statusText / `Request failed with status …` never reach the user.
//
// ===========================================================================
// NEVER RENDER PART OF A SECRET
// ===========================================================================
// task-01 removed a live leak where the API returned the first 8 characters of
// a signing secret. Nothing here derives a display string from a secret: the
// key table shows the server's `prefix` (public by construction) and the
// webhook card shows the CONSTANT mask the server sends in `secret_masked`.
// The only place a full secret is ever painted is the show-once dialog, from a
// value that exists in memory for the lifetime of that dialog and is never put
// into a query cache.
//
// vg-edge-case: E-38 (all three cards empty -> an empty state whose CTA depends
// on the ROLE: an owner is invited to create the first key, a staff member is
// told to ask the owner. Same data, different next action.)
// vg-edge-case: E-39 (a failed handshake is a 200 with
// `handshake.failure_reason`, so the cause is shown INLINE under the URL field
// — timeout / challenge_mismatch / tls / dns — with a Retry that re-runs the
// same request. Every mutation has onError + toast.)
// vg-edge-case: E-40 (the show-once dialog blocks ESC and outside-click via the
// props task-09 forwarded on `Dialog`, and gates Done behind an explicit "I
// have saved this key" checkbox. The built-in X is kept because the mockup
// draws it, but it routes through the same gate: it cannot destroy an
// unsaved key either. Clipboard failure falls back to selecting the text so a
// vendor in an insecure context can still copy by hand.)
// vg-edge-case: E-41 (the webhook card renders the server's status verbatim and
// refetches every 30s while the tab is visible, with an "updated at HH:mm"
// badge and a manual Refresh. `auto_disabled` is its own badge — it is never
// flattened into `verified`, which would be the card lying about delivery.)
// vg-edge-case: E-42 (staff see every row but no enabled control. The server
// gate is the real one — TS-12b asserts a staff session gets 403 — this is
// only the affordance.)

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type JSX,
  type ReactNode,
} from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';

import {
  Button,
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ConfirmDialog,
  Input,
  NativeSelect,
  PageHeader,
  SearchInput,
  SettingsLayout,
  Spinner,
  useToast,
  useVendorStaffRole,
} from '@printway/ui';

import { csrfFetch } from '../lib/api/csrfFetch';
import { useTranslation } from '../lib/i18n/index.js';
import { NOTICE_CLASS, STATUS_PILL_CLASS } from '../lib/ui/statusPill.js';

// ---------------------------------------------------------------------------
// Wire types — copied from API-CONTRACTS (management lane, task-29)
// ---------------------------------------------------------------------------

const BASE = '/api/v1/vendor/integration';

type KeyStatus = 'active' | 'revoked';
type CreatorRole = 'vendor_owner' | 'admin';

interface KeyRow {
  key_id: string;
  /** Masked display form (`pwv_6a1f…`). Public by construction — never a secret. */
  prefix: string;
  created_by: {
    user_id: string | null;
    /** Empty when the creator's user row is gone — the portal renders its own copy. */
    display_name: string;
    role: CreatorRole;
  };
  created_by_role: CreatorRole;
  status: KeyStatus;
  last_used_at: string | null;
  created_at: string;
  revoked_at: string | null;
}

interface KeysMeta {
  live_count: number;
  max_live: number;
  /**
   * P18.D-16 — the two kill switches, mirrored from the admin lane.
   *
   * Optional because they are additive (P18.D-19) and an older API will not
   * send them; absent is treated as "cannot tell", which renders no banner
   * rather than a wrong one.
   *
   * `blocked_by` names the OUTER switch first: during an incident the vendor
   * should read "the integration is switched off platform-wide", not "your
   * account is off", because those two sentences send them to different places.
   */
  lane_enabled?: boolean;
  vendor_enabled?: boolean;
  effective_enabled?: boolean;
  blocked_by?: 'lane' | 'vendor' | null;
}

interface CreatedKeyData {
  key_id: string;
  prefix: string;
  /** SHOW-ONCE. Held in component state only; never written to the query cache. */
  plaintext: string;
  created_at: string;
}

type WebhookStatus = 'unset' | 'unverified' | 'verified' | 'auto_disabled';
type HandshakeFailureReason = 'timeout' | 'challenge_mismatch' | 'tls' | 'dns';

const HANDSHAKE_REASONS: readonly HandshakeFailureReason[] = [
  'timeout',
  'challenge_mismatch',
  'tls',
  'dns',
];

interface WebhookData {
  url: string | null;
  status: WebhookStatus;
  verified_at: string | null;
  consecutive_failures: number;
  /** Constant mask (`whsec_••••`) — not derived from the plaintext. */
  secret_masked: string | null;
  rotation: { in_window: boolean; window_expires_at: string | null };
  last_delivery_at: string | null;
}

interface PutWebhookData {
  url: string;
  status: 'unverified' | 'verified';
  verified_at: string | null;
  handshake: { ok: boolean; failure_reason: HandshakeFailureReason | null };
}

interface RotateSecretData {
  /** SHOW-ONCE. Same handling as a created key plaintext. */
  new_secret: string;
  window_expires_at: string | null;
  dual_sign_active: boolean;
}

type DeliveryStatus = 'delivered' | 'retrying' | 'failed' | 'suppressed';

const DELIVERY_STATUSES: readonly DeliveryStatus[] = [
  'delivered',
  'retrying',
  'failed',
  'suppressed',
];

interface DeliveryRow {
  delivery_id: string;
  event_id: string;
  event_type: string;
  status: DeliveryStatus;
  attempt: number;
  max_attempts: number;
  response_code: number | null;
  last_attempt_at: string | null;
  created_at: string | null;
}

interface DeliveriesMeta {
  next_cursor: string | null;
  limit: number;
}

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

/**
 * Every code the management lane can put on the wire —
 * `VendorManagementErrorCode` (contracts/errors.ts, task-05) enumerated here so
 * a code without catalog copy is a TYPE error rather than a raw key rendered at
 * a vendor. Kept in the contract's order for a readable diff.
 */
const MANAGEMENT_ERROR_CODES = [
  'AUTH_REQUIRED',
  'FORBIDDEN',
  'NOT_FOUND',
  'VALIDATION_FAILED',
  'IDEMPOTENCY_CONFLICT',
  'RATE_LIMITED',
  'PAYLOAD_TOO_LARGE',
  'SERVICE_UNAVAILABLE',
  'CONFLICT_VERSION',
  'SUBSTEP_INVALID',
  'INVALID_TRANSITION',
  'ASSIGNMENT_SUPERSEDED',
  'BACK_ORDER_NOT_ENABLED',
  'CHANGE_REQUIRES_APPROVAL',
  'PRODUCT_PENDING_APPROVAL',
  'CURSOR_EXPIRED',
  'SSRF_BLOCKED',
  'AUTH_CSRF_MISSING',
  'ALREADY_REVOKED',
  'KEY_LIMIT_REACHED',
  'ROTATION_IN_PROGRESS',
] as const;

type ManagementErrorCode = (typeof MANAGEMENT_ERROR_CODES)[number];

const MANAGEMENT_ERROR_CODE_SET: ReadonlySet<string> = new Set(
  MANAGEMENT_ERROR_CODES,
);

function isManagementErrorCode(value: string): value is ManagementErrorCode {
  return MANAGEMENT_ERROR_CODE_SET.has(value);
}

/** Local sentinel — a request that never reached the API (offline, DNS, abort). */
const NETWORK_CODE = 'NETWORK';

class IntegrationApiError extends Error {
  readonly code: string;
  readonly status: number;
  /** `details.i18n_key` as sent by task-29, e.g. `integration.errors.FORBIDDEN`. */
  readonly i18nKey: string | undefined;
  readonly userMessage: string | undefined;
  readonly fieldErrors: Record<string, string[]> | undefined;

  constructor(
    code: string,
    message: string,
    status: number,
    extras: {
      i18nKey?: string;
      userMessage?: string;
      fieldErrors?: Record<string, string[]>;
    } = {},
  ) {
    super(message);
    this.name = 'IntegrationApiError';
    this.code = code;
    this.status = status;
    this.i18nKey = extras.i18nKey;
    this.userMessage = extras.userMessage;
    this.fieldErrors = extras.fieldErrors;
  }
}

interface WireOk<TData, TMeta> {
  ok: true;
  data: TData;
  meta?: TMeta;
  message?: string;
}

interface WireErr {
  ok: false;
  error: {
    code: string;
    message: string;
    user_message?: string;
    details?: { i18n_key?: string } & Record<string, unknown>;
    field_errors?: Record<string, string[]>;
  };
}

async function request<TData, TMeta = undefined>(
  path: string,
  init: RequestInit = {},
): Promise<{ data: TData; meta: TMeta | undefined }> {
  let res: Response;
  try {
    res = await csrfFetch(`${BASE}${path}`, init);
  } catch (err) {
    // Transport-level failure: there is no body to read a code out of, so the
    // FE standard's `network_fallback` branch is the honest answer. The cause is
    // preserved on the message for the console, never for the toast.
    throw new IntegrationApiError(
      NETWORK_CODE,
      err instanceof Error ? err.message : 'network failure',
      0,
    );
  }

  const body = (await res.json().catch(() => null)) as
    | WireOk<TData, TMeta>
    | WireErr
    | null;

  if (body === null) {
    throw new IntegrationApiError(NETWORK_CODE, 'empty response body', res.status);
  }
  if (body.ok !== true) {
    const wire = body.error;
    throw new IntegrationApiError(
      wire?.code ?? 'UNKNOWN',
      wire?.message ?? 'request failed',
      res.status,
      {
        ...(wire?.details?.i18n_key !== undefined
          ? { i18nKey: wire.details.i18n_key }
          : {}),
        ...(wire?.user_message !== undefined
          ? { userMessage: wire.user_message }
          : {}),
        ...(wire?.field_errors !== undefined
          ? { fieldErrors: wire.field_errors }
          : {}),
      },
    );
  }
  return { data: body.data, meta: body.meta };
}

/**
 * `integration.errors.SSRF_BLOCKED` -> `SSRF_BLOCKED`.
 *
 * The server sends the key NAMESPACE-QUALIFIED because it does not know how the
 * portal scopes its catalog; `useTranslation('integration')` already put us
 * inside that namespace, so the prefix has to come off or i18next would look
 * for a nested `integration.errors.…` path that does not exist.
 */
function codeFromI18nKey(key: string | undefined): string | null {
  if (key === undefined) return null;
  const marker = 'errors.';
  const at = key.lastIndexOf(marker);
  if (at < 0) return null;
  return key.slice(at + marker.length);
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** The message-priority chain from the file header, in one place. */
function resolveErrorMessage(error: unknown, t: Translate): string {
  if (!(error instanceof IntegrationApiError)) {
    return t('errors.UNKNOWN');
  }
  if (error.userMessage !== undefined && error.userMessage.length > 0) {
    return error.userMessage;
  }
  if (error.code === NETWORK_CODE) {
    return t('common.networkError');
  }
  const code = codeFromI18nKey(error.i18nKey) ?? error.code;
  if (isManagementErrorCode(code)) {
    return t(`errors.${code}`);
  }
  // A code this build has no copy for: the frozen English developer default is
  // still more useful than a generic sentence, and the catalog gap is visible.
  if (error.message.length > 0) return error.message;
  return t('errors.UNKNOWN');
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------
//
// `undefined` locale everywhere — the browser's locale decides. A hardcoded
// 'vi-VN' / 'en-US' would survive the language switch the TopBar offers.
// `dateStyle`/`timeStyle` are never mixed with component options in one options
// object: V8 throws `Invalid option` on that combination and the page goes
// blank (dbg-20260706-122118-9726).

function formatDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  return at.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatDateTime(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  return at.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function formatClock(epochMs: number): string {
  return new Date(epochMs).toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

const KEYS_QUERY_KEY = ['vendor', 'integration', 'keys'] as const;
const WEBHOOK_QUERY_KEY = ['vendor', 'integration', 'webhook'] as const;

/**
 * The `/keys` read, in ONE place.
 *
 * Both the keys card and the page-level lane banner need it, and TanStack
 * dedupes by query key — so calling this twice is one request, not two. Written
 * as a shared hook rather than lifting state through props because the banner
 * belongs at the top of the page while the data belongs to the card.
 */
function useKeysQuery(): UseQueryResult<
  { rows: KeyRow[]; meta: KeysMeta },
  IntegrationApiError
> {
  return useQuery({
    queryKey: KEYS_QUERY_KEY,
    queryFn: async () => {
      const { data, meta } = await request<KeyRow[], KeysMeta>('/keys');
      return { rows: data, meta: meta ?? { live_count: 0, max_live: 0 } };
    },
  });
}
const DELIVERIES_QUERY_KEY = ['vendor', 'integration', 'deliveries'] as const;

/** P18.D-22 — the card must not out-live the server's own state by more than this. */
const REFETCH_MS = 30_000;

// ---------------------------------------------------------------------------
// Icons
// ---------------------------------------------------------------------------
//
// Inlined rather than imported: `apps/vendor` does not depend on `lucide-react`
// (see package.json), and `VendorSidebar.tsx` draws its own glyphs for the same
// reason. Paths are the lucide originals so the page matches the icon language
// the rest of the portal already speaks.

function IconPlus(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4"
      aria-hidden="true"
    >
      <path d="M5 12h14" />
      <path d="M12 5v14" />
    </svg>
  );
}

function IconRefresh(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4"
      aria-hidden="true"
    >
      <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
      <path d="M21 3v5h-5" />
      <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
      <path d="M8 16H3v5" />
    </svg>
  );
}

function IconCopy(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4"
      aria-hidden="true"
    >
      <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
      <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
    </svg>
  );
}

function IconCheckCircle(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5 text-green-600 dark:text-green-400"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );
}

function IconAlertTriangle(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="mt-0.5 h-4 w-4 shrink-0"
      aria-hidden="true"
    >
      <path d="m10.29 3.86-8.47 14.14A2 2 0 0 0 3.53 21h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
      <path d="M12 9v4" />
      <path d="M12 17h.01" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Small shared pieces
// ---------------------------------------------------------------------------

function CardShell({
  title,
  description,
  action,
  children,
  testId,
}: {
  title: string;
  description: string;
  action?: ReactNode;
  children: ReactNode;
  testId: string;
}): JSX.Element {
  return (
    <section
      data-testid={testId}
      className="rounded-2xl border border-pw-border bg-pw-bg p-5 shadow-sm"
    >
      <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-pw-text">{title}</h2>
          <p className="mt-1 text-sm text-pw-text-muted">{description}</p>
        </div>
        {action ?? null}
      </header>
      {children}
    </section>
  );
}

function EmptyState({
  title,
  description,
  action,
  testId,
}: {
  title: string;
  description: string;
  action?: ReactNode;
  testId: string;
}): JSX.Element {
  return (
    <div
      data-testid={testId}
      className="rounded-xl border border-dashed border-pw-border bg-pw-surface px-4 py-10 text-center"
    >
      <p className="text-sm font-medium text-pw-text">{title}</p>
      <p className="mx-auto mt-1 max-w-md text-sm text-pw-text-muted">
        {description}
      </p>
      {action ? <div className="mt-4 flex justify-center">{action}</div> : null}
    </div>
  );
}

/**
 * "Updated at HH:mm" + a manual Refresh (P18.D-22 point 4).
 *
 * `isFetching`, not `isLoading`: `isLoading` is only true on the very first
 * fetch, so a 30s background refetch would be invisible and the vendor would
 * read stale rows as current.
 */
function FreshnessBadge({
  updatedAt,
  isFetching,
  onRefresh,
  t,
  testId,
}: {
  updatedAt: number;
  isFetching: boolean;
  onRefresh: () => void;
  t: Translate;
  testId: string;
}): JSX.Element {
  return (
    <div className="flex items-center gap-2">
      <span
        data-testid={testId}
        className="text-xs text-pw-text-muted"
        aria-live="polite"
      >
        {updatedAt > 0
          ? t('common.updatedAt', { time: formatClock(updatedAt) })
          : t('common.loading')}
      </span>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={onRefresh}
        disabled={isFetching}
        data-testid={`${testId}-refresh`}
      >
        {isFetching ? (
          <Spinner size="sm" label={t('common.loading')} />
        ) : (
          <IconRefresh />
        )}
        <span>{t('common.refresh')}</span>
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Show-once dialog (E-40)
// ---------------------------------------------------------------------------

interface ShowOnceDialogProps {
  open: boolean;
  title: string;
  description: string;
  warning: string;
  value: string;
  onClose: () => void;
  t: Translate;
  testId: string;
}

/**
 * The one dialog in the portal that must not close by accident.
 *
 * Three exits exist on a Radix dialog — ESC, an outside pointer-down, and the
 * built-in X — and all three are routed through the SAME gate (`saved`). ESC and
 * outside-click are `preventDefault()`ed on the props task-09 forwarded; the X
 * stays visible because the mockup draws it, but it fires `onOpenChange(false)`
 * which this component ignores until the checkbox is ticked. Hiding the X would
 * have been the easier fix and the wrong one: an invisible exit is not a gate,
 * and the mockup is the contract for what the vendor sees.
 */
function ShowOnceDialog({
  open,
  title,
  description,
  warning,
  value,
  onClose,
  t,
  testId,
}: ShowOnceDialogProps): JSX.Element {
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const valueRef = useRef<HTMLInputElement | null>(null);

  // A second mint re-opens the dialog with a new value — reset the gate, or the
  // previous "I have saved this" would silently apply to a key nobody copied.
  useEffect(() => {
    if (open) {
      setSaved(false);
      setCopied(false);
      setCopyFailed(false);
    }
  }, [open, value]);

  const copy = useCallback(async (): Promise<void> => {
    try {
      const clipboard =
        typeof navigator !== 'undefined' ? navigator.clipboard : undefined;
      if (!clipboard?.writeText) {
        throw new Error('clipboard unavailable');
      }
      await clipboard.writeText(value);
      setCopied(true);
      setCopyFailed(false);
    } catch (err) {
      // Insecure context, denied permission, or no Clipboard API at all. The
      // fallback the decision asks for: select the text so the vendor can copy
      // it by hand rather than lose a value that is never shown again.
      // eslint-disable-next-line no-console -- diagnosis for a silent-looking button
      console.warn('[integration] clipboard copy failed', err);
      setCopied(false);
      setCopyFailed(true);
      const field = valueRef.current;
      if (field) {
        field.focus();
        field.select();
      }
    }
  }, [value]);

  return (
    <Dialog
      open={open}
      // The X routes here too. Ignoring a close request while `saved` is false
      // is what makes the checkbox a gate rather than a suggestion.
      onOpenChange={(next) => {
        if (!next && saved) onClose();
      }}
      onEscapeKeyDown={(event) => {
        event.preventDefault();
      }}
      onInteractOutside={(event) => {
        event.preventDefault();
      }}
      className="max-w-xl"
    >
      <div data-testid={testId}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <IconCheckCircle />
            <span>{title}</span>
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="mt-4 flex items-center gap-2">
          <Input
            ref={valueRef}
            size="default"
            readOnly
            value={value}
            aria-label={t('showOnce.valueAria')}
            data-testid={`${testId}-value`}
            className="font-mono text-[13px]"
            onFocus={(event) => {
              event.currentTarget.select();
            }}
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              void copy();
            }}
            data-testid={`${testId}-copy`}
          >
            <IconCopy />
            <span>{copied ? t('showOnce.copied') : t('showOnce.copy')}</span>
          </Button>
        </div>

        {copyFailed ? (
          <p
            data-testid={`${testId}-copy-failed`}
            className={`mt-2 rounded-md px-3 py-2 text-xs ${NOTICE_CLASS.warning}`}
          >
            {t('showOnce.copyFailed')}
          </p>
        ) : null}

        <p
          className={`mt-3 flex items-start gap-2 rounded-md px-3 py-2 text-xs ${NOTICE_CLASS.warning}`}
        >
          <IconAlertTriangle />
          <span>{warning}</span>
        </p>

        <label className="mt-4 flex items-center gap-2 text-sm text-pw-text">
          <input
            type="checkbox"
            checked={saved}
            onChange={(event) => {
              setSaved(event.target.checked);
            }}
            data-testid={`${testId}-saved`}
            // `focus-visible:outline-none` is NOT paired with a replacement
            // here. The project's focus rule is about styled controls whose
            // border we own; this is a bare `<input type=checkbox>` and the repo
            // ships no @tailwindcss/forms, so the browser keeps `appearance:
            // auto` and `border-*` paints nothing on it. Removing the outline
            // therefore removed the ONLY focus indicator — on the one control
            // that unlocks "Done" for a secret shown exactly once. `accent-*`
            // colours the box itself, and the UA outline stays.
            className="h-4 w-4 rounded border-pw-border accent-pw-primary"
          />
          <span>{t('showOnce.savedCheckbox')}</span>
        </label>

        <DialogFooter className="mt-5">
          <Button
            type="button"
            className="w-full"
            disabled={!saved}
            onClick={onClose}
            data-testid={`${testId}-done`}
            title={saved ? undefined : t('showOnce.blockedHint')}
          >
            {t('showOnce.done')}
          </Button>
        </DialogFooter>
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Card 1 — API keys
// ---------------------------------------------------------------------------

interface RoleView {
  canManage: boolean;
  vendorId: string | null;
  isMultiVendor: boolean;
}

function IntegrationKeysCard({ role }: { role: RoleView }): JSX.Element {
  const { t } = useTranslation('integration');
  const { toast } = useToast();
  const qc = useQueryClient();

  const [formOpen, setFormOpen] = useState(false);
  const [label, setLabel] = useState('');
  const [labelError, setLabelError] = useState<string | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<KeyRow | null>(null);
  const [createdKey, setCreatedKey] = useState<CreatedKeyData | null>(null);

  const keys = useKeysQuery();

  const createKey = useMutation<CreatedKeyData, IntegrationApiError, string>({
    mutationFn: async (nextLabel) => {
      const { data } = await request<CreatedKeyData>('/keys', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          label: nextLabel,
          // P18.D-02 — an owner of several vendors must SAY which one. The
          // server treats this as a confirmation of the session scope, never as
          // an authorisation override, so sending it for a single-vendor owner
          // would be noise.
          ...(role.isMultiVendor && role.vendorId !== null
            ? { vendor_id: role.vendorId }
            : {}),
        }),
      });
      return data;
    },
    onSuccess: (data) => {
      setFormOpen(false);
      setLabel('');
      setLabelError(null);
      // The plaintext goes to component state ONLY — putting it in the query
      // cache would keep a show-once secret alive across navigations.
      setCreatedKey(data);
      toast({ variant: 'success', title: t('keys.toast.created') });
      void qc.invalidateQueries({ queryKey: KEYS_QUERY_KEY });
    },
    onError: (error) => {
      const message = resolveErrorMessage(error, t);
      // Field-level failures belong beside the field (INTERFACE-STANDARDS
      // § field_error_rule); everything else is a toast.
      if (error.fieldErrors?.['label'] !== undefined) {
        setLabelError(message);
        return;
      }
      toast({ variant: 'error', title: message });
    },
  });

  const revokeKey = useMutation<{ key_id: string }, IntegrationApiError, string>({
    mutationFn: async (keyId) => {
      const { data } = await request<{ key_id: string }>(
        `/keys/${encodeURIComponent(keyId)}`,
        { method: 'DELETE' },
      );
      return data;
    },
    onSuccess: () => {
      setRevokeTarget(null);
      toast({ variant: 'success', title: t('keys.toast.revoked') });
      void qc.invalidateQueries({ queryKey: KEYS_QUERY_KEY });
    },
    onError: (error) => {
      setRevokeTarget(null);
      toast({ variant: 'error', title: resolveErrorMessage(error, t) });
      // ALREADY_REVOKED means our table is behind the server — refetch rather
      // than leave a row the vendor can click again.
      void qc.invalidateQueries({ queryKey: KEYS_QUERY_KEY });
    },
  });

  const rows = keys.data?.rows ?? [];
  const meta = keys.data?.meta;
  const atLimit =
    meta !== undefined && meta.max_live > 0 && meta.live_count >= meta.max_live;

  const submitCreate = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const trimmed = label.trim();
    if (trimmed.length === 0) {
      setLabelError(t('errors.VALIDATION_FAILED'));
      return;
    }
    setLabelError(null);
    createKey.mutate(trimmed);
  };

  const createButton = (
    <Button
      type="button"
      onClick={() => {
        setFormOpen(true);
      }}
      disabled={!role.canManage || atLimit}
      data-testid="integration-keys-create"
    >
      <IconPlus />
      <span>{t('keys.create')}</span>
    </Button>
  );

  return (
    <CardShell
      testId="integration-keys-card"
      title={t('keys.title')}
      description={t('keys.subtitle')}
      action={
        // The webhook and delivery cards both carry a FreshnessBadge; this one
        // did not, and it is the card where its absence bites hardest. Revoking
        // a key toasts "revoked" and then invalidates — during the refetch the
        // row still reads Active with a live Revoke button next to it. Keyed on
        // `isFetching`, NOT `isLoading`: `isLoading` is only true on the FIRST
        // load, so a background refetch renders stale data with no indication
        // at all. That is the literal case the project's mutation rule cites.
        <div className="flex items-center gap-2">
          <FreshnessBadge
            testId="integration-keys-freshness"
            updatedAt={keys.dataUpdatedAt}
            isFetching={keys.isFetching}
            onRefresh={() => {
              void qc.invalidateQueries({ queryKey: KEYS_QUERY_KEY });
            }}
            t={t}
          />
          {createButton}
        </div>
      }
    >
      {meta !== undefined ? (
        <p className="mb-3 text-xs text-pw-text-muted" data-testid="integration-keys-limit">
          {t('keys.limit', { live: meta.live_count, max: meta.max_live })}
          {atLimit && role.canManage ? ` — ${t('keys.limitReached')}` : ''}
        </p>
      ) : null}

      {!role.canManage ? (
        <p className={`mb-3 rounded-md px-3 py-2 text-xs ${NOTICE_CLASS.neutral}`}>
          {t('common.readOnlyHint')}
        </p>
      ) : null}

      {keys.isLoading ? (
        <div className="flex items-center gap-2 py-8 text-sm text-pw-text-muted">
          <Spinner size="sm" label={t('common.loading')} />
          <span>{t('common.loading')}</span>
        </div>
      ) : keys.isError ? (
        <p
          data-testid="integration-keys-error"
          className={`rounded-md px-3 py-2 text-sm ${NOTICE_CLASS.danger}`}
        >
          {resolveErrorMessage(keys.error, t)}
        </p>
      ) : rows.length === 0 ? (
        <EmptyState
          testId="integration-keys-empty"
          title={role.canManage ? t('keys.empty.ownerTitle') : t('keys.empty.staffTitle')}
          description={
            role.canManage
              ? t('keys.empty.ownerDescription')
              : t('keys.empty.staffDescription')
          }
          action={
            role.canManage ? (
              <Button
                type="button"
                onClick={() => {
                  setFormOpen(true);
                }}
                data-testid="integration-keys-empty-cta"
              >
                <IconPlus />
                <span>{t('keys.empty.ownerCta')}</span>
              </Button>
            ) : null
          }
        />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-pw-border">
          <table className="w-full text-sm">
            <thead className="bg-pw-surface text-xs uppercase tracking-wider text-pw-text-muted">
              <tr>
                <th className="px-3 py-2 text-left">{t('keys.table.key')}</th>
                <th className="px-3 py-2 text-left">{t('keys.table.created')}</th>
                <th className="px-3 py-2 text-left">{t('keys.table.lastUsed')}</th>
                <th className="px-3 py-2 text-left">{t('keys.table.createdBy')}</th>
                <th className="px-3 py-2 text-left">{t('keys.table.status')}</th>
                <th className="px-3 py-2 text-left">{t('keys.table.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const creator =
                  row.created_by.display_name.length > 0
                    ? row.created_by.display_name
                    : t('common.unknownMember');
                return (
                  <tr
                    key={row.key_id}
                    data-testid={`integration-key-row-${row.key_id}`}
                    className="border-t border-pw-border"
                  >
                    <td className="px-3 py-2 font-mono text-[13px] text-pw-text">
                      {row.prefix}
                    </td>
                    <td className="px-3 py-2 text-pw-text-muted">
                      {formatDate(row.created_at) ?? '—'}
                    </td>
                    <td className="px-3 py-2 text-pw-text-muted">
                      {formatDate(row.last_used_at) ?? t('common.never')}
                    </td>
                    <td className="px-3 py-2 text-pw-text-muted">
                      <span>{creator}</span>
                      {row.created_by_role === 'admin' ? (
                        <span
                          className={`ml-2 rounded px-1.5 py-0.5 text-[11px] font-medium ${STATUS_PILL_CLASS.info}`}
                        >
                          {t('keys.viaAdmin')}
                        </span>
                      ) : null}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`rounded px-1.5 py-0.5 text-xs font-semibold ${
                          row.status === 'active'
                            ? STATUS_PILL_CLASS.success
                            : STATUS_PILL_CLASS.done
                        }`}
                      >
                        {t(`keys.status.${row.status}`)}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      {row.status === 'active' ? (
                        <button
                          type="button"
                          disabled={!role.canManage || revokeKey.isPending}
                          onClick={() => {
                            setRevokeTarget(row);
                          }}
                          data-testid={`integration-key-revoke-${row.key_id}`}
                          className="text-sm font-medium text-red-600 hover:underline disabled:cursor-not-allowed disabled:opacity-50 dark:text-red-300"
                        >
                          {t('keys.revoke')}
                        </button>
                      ) : (
                        <span className="text-sm text-pw-text-muted">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog
        open={formOpen}
        onOpenChange={(next) => {
          if (!next) {
            setFormOpen(false);
            setLabelError(null);
          }
        }}
      >
        <form onSubmit={submitCreate} data-testid="integration-key-create-form">
          <DialogHeader>
            <DialogTitle>{t('keys.create')}</DialogTitle>
            <DialogDescription>{t('keys.labelHint')}</DialogDescription>
          </DialogHeader>
          <label className="mt-4 flex flex-col gap-1">
            <span className="text-sm font-medium text-pw-text">
              {t('keys.labelField')}
            </span>
            <Input
              size="default"
              value={label}
              maxLength={64}
              placeholder={t('keys.labelPlaceholder')}
              onChange={(event) => {
                setLabel(event.target.value);
              }}
              data-testid="integration-key-label-input"
            />
          </label>
          {labelError !== null ? (
            <p
              data-testid="integration-key-label-error"
              className={`mt-2 rounded-md px-3 py-2 text-xs ${NOTICE_CLASS.danger}`}
            >
              {labelError}
            </p>
          ) : null}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setFormOpen(false);
                setLabelError(null);
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={createKey.isPending}
              data-testid="integration-key-create-submit"
            >
              {createKey.isPending ? (
                <Spinner size="sm" label={t('keys.creating')} />
              ) : null}
              <span>{createKey.isPending ? t('keys.creating') : t('keys.submit')}</span>
            </Button>
          </DialogFooter>
        </form>
      </Dialog>

      <ConfirmDialog
        open={revokeTarget !== null}
        onOpenChange={(next) => {
          if (!next) setRevokeTarget(null);
        }}
        type="destructive"
        title={t('keys.revokeConfirm.title')}
        description={t('keys.revokeConfirm.description')}
        confirmLabel={t('keys.revokeConfirm.confirm')}
        cancelLabel={t('common.cancel')}
        loading={revokeKey.isPending}
        onConfirm={() => {
          if (revokeTarget !== null) revokeKey.mutate(revokeTarget.key_id);
        }}
      />

      <ShowOnceDialog
        open={createdKey !== null}
        testId="integration-key-created-modal"
        title={t('showOnce.keyTitle')}
        description={t('showOnce.keyDescription')}
        warning={t('showOnce.warningKey')}
        value={createdKey?.plaintext ?? ''}
        onClose={() => {
          setCreatedKey(null);
        }}
        t={t}
      />
    </CardShell>
  );
}

// ---------------------------------------------------------------------------
// Card 2 — webhook endpoint
// ---------------------------------------------------------------------------

const WEBHOOK_STATUS_PILL: Record<WebhookStatus, string> = {
  unset: STATUS_PILL_CLASS.neutral,
  unverified: STATUS_PILL_CLASS.warning,
  verified: STATUS_PILL_CLASS.success,
  auto_disabled: STATUS_PILL_CLASS.danger,
};

function isHandshakeReason(value: string): value is HandshakeFailureReason {
  return (HANDSHAKE_REASONS as readonly string[]).includes(value);
}

function WebhookCard({ role }: { role: RoleView }): JSX.Element {
  const { t } = useTranslation('integration');
  const { toast } = useToast();
  const qc = useQueryClient();

  const [url, setUrl] = useState('');
  const [urlTouched, setUrlTouched] = useState(false);
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [handshakeReason, setHandshakeReason] = useState<string | null>(null);
  const [rotateOpen, setRotateOpen] = useState(false);
  const [rotatedSecret, setRotatedSecret] = useState<string | null>(null);

  const webhook: UseQueryResult<WebhookData, IntegrationApiError> = useQuery({
    queryKey: WEBHOOK_QUERY_KEY,
    queryFn: async () => {
      const { data } = await request<WebhookData>('/webhook');
      return data;
    },
    // P18.D-22 / E-41 — the badge must not out-live the server's state. Only
    // while the tab is actually in front of someone.
    refetchInterval: REFETCH_MS,
    refetchIntervalInBackground: false,
  });

  // Seed the field from the server exactly once per loaded URL, and never over
  // the vendor's own typing (`urlTouched`) — otherwise a 30s refetch would wipe
  // a half-typed address.
  const serverUrl = webhook.data?.url ?? '';
  useEffect(() => {
    if (!urlTouched) setUrl(serverUrl);
  }, [serverUrl, urlTouched]);

  const saveWebhook = useMutation<PutWebhookData, IntegrationApiError, string>({
    mutationFn: async (nextUrl) => {
      const { data } = await request<PutWebhookData>('/webhook', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: nextUrl }),
      });
      return data;
    },
    onSuccess: (data) => {
      setInlineError(null);
      // Seed from the RESPONSE before releasing `urlTouched`. Clearing the flag
      // first re-armed the effect above while `serverUrl` still held the
      // pre-save value, so the field visibly snapped back to the OLD url and
      // stayed there until the invalidate's refetch landed — a green "webhook
      // verified and saved" toast sitting next to the address the vendor just
      // replaced, which reads as a lost save and invites a re-submit.
      if (typeof data.url === 'string' && data.url.length > 0) {
        setUrl(data.url);
      }
      setUrlTouched(false);
      // A FAILED HANDSHAKE IS A 200 (contract Block 3). The cause goes inline
      // under the field with a Retry — not into a toast the vendor dismisses
      // before reading which of the four causes it was.
      if (data.handshake.ok) {
        setHandshakeReason(null);
        toast({ variant: 'success', title: t('webhook.toast.verified') });
      } else {
        setHandshakeReason(data.handshake.failure_reason ?? 'unknown');
        toast({ variant: 'warning', title: t('webhook.toast.unverified') });
      }
      void qc.invalidateQueries({ queryKey: WEBHOOK_QUERY_KEY });
      void qc.invalidateQueries({ queryKey: DELIVERIES_QUERY_KEY });
    },
    onError: (error) => {
      setHandshakeReason(null);
      const message = resolveErrorMessage(error, t);
      // `422 SSRF_BLOCKED` and `400 VALIDATION_FAILED` are both about the URL
      // the vendor just typed, so both belong beside that field.
      if (
        error.fieldErrors?.['url'] !== undefined ||
        error.code === 'SSRF_BLOCKED' ||
        error.code === 'VALIDATION_FAILED'
      ) {
        setInlineError(message);
        return;
      }
      setInlineError(null);
      toast({ variant: 'error', title: message });
    },
  });

  const rotateSecret = useMutation<RotateSecretData, IntegrationApiError, void>({
    mutationFn: async () => {
      const { data } = await request<RotateSecretData>('/webhook/rotate-secret', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ revoke_old: false }),
      });
      return data;
    },
    onSuccess: (data) => {
      setRotateOpen(false);
      setRotatedSecret(data.new_secret);
      toast({ variant: 'success', title: t('webhook.toast.rotated') });
      void qc.invalidateQueries({ queryKey: WEBHOOK_QUERY_KEY });
    },
    onError: (error) => {
      setRotateOpen(false);
      toast({ variant: 'error', title: resolveErrorMessage(error, t) });
    },
  });

  const data = webhook.data;
  const status: WebhookStatus = data?.status ?? 'unset';
  const verifiedAt = formatDateTime(data?.verified_at);
  const lastDeliveryAt = formatDateTime(data?.last_delivery_at);
  const windowExpiresAt = formatDateTime(data?.rotation.window_expires_at);
  const canSubmit = role.canManage && url.trim().length > 0 && !saveWebhook.isPending;

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (!canSubmit) return;
    saveWebhook.mutate(url.trim());
  };

  const handshakeCopy =
    handshakeReason === null
      ? null
      : isHandshakeReason(handshakeReason)
        ? t(`webhook.handshakeFailed.${handshakeReason}`)
        : t('webhook.handshakeFailed.unknown');

  return (
    <CardShell
      testId="integration-webhook-card"
      title={t('webhook.title')}
      description={t('webhook.subtitle')}
      action={
        <FreshnessBadge
          testId="integration-webhook-freshness"
          updatedAt={webhook.dataUpdatedAt}
          isFetching={webhook.isFetching}
          onRefresh={() => {
            void qc.invalidateQueries({ queryKey: WEBHOOK_QUERY_KEY });
          }}
          t={t}
        />
      }
    >
      {webhook.isLoading ? (
        <div className="flex items-center gap-2 py-8 text-sm text-pw-text-muted">
          <Spinner size="sm" label={t('common.loading')} />
          <span>{t('common.loading')}</span>
        </div>
      ) : webhook.isError ? (
        <p
          data-testid="integration-webhook-error"
          className={`rounded-md px-3 py-2 text-sm ${NOTICE_CLASS.danger}`}
        >
          {resolveErrorMessage(webhook.error, t)}
        </p>
      ) : (
        <div className="space-y-4">
          {status === 'unset' ? (
            <EmptyState
              testId="integration-webhook-empty"
              title={
                role.canManage
                  ? t('webhook.empty.ownerTitle')
                  : t('webhook.empty.staffTitle')
              }
              description={
                role.canManage
                  ? t('webhook.empty.ownerDescription')
                  : t('webhook.empty.staffDescription')
              }
            />
          ) : null}

          <form onSubmit={submit} className="space-y-2">
            <label className="flex flex-col gap-1">
              <span className="text-sm font-medium text-pw-text">
                {t('webhook.urlLabel')}
              </span>
              <div className="flex items-center gap-2">
                <Input
                  size="default"
                  type="url"
                  value={url}
                  disabled={!role.canManage}
                  placeholder={t('webhook.urlPlaceholder')}
                  onChange={(event) => {
                    setUrlTouched(true);
                    setUrl(event.target.value);
                  }}
                  data-testid="integration-webhook-url"
                />
                <Button
                  type="submit"
                  disabled={!canSubmit}
                  data-testid="integration-webhook-verify"
                >
                  {saveWebhook.isPending ? (
                    <Spinner size="sm" label={t('webhook.verifying')} />
                  ) : null}
                  <span>
                    {saveWebhook.isPending
                      ? t('webhook.verifying')
                      : status === 'auto_disabled' || status === 'unverified'
                        ? t('webhook.reverify')
                        : t('webhook.verifySave')}
                  </span>
                </Button>
              </div>
            </label>

            {inlineError !== null ? (
              <div
                data-testid="integration-webhook-url-error"
                className={`flex flex-wrap items-center justify-between gap-2 rounded-md px-3 py-2 text-xs ${NOTICE_CLASS.danger}`}
              >
                <span>{inlineError}</span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={!canSubmit}
                  onClick={() => {
                    saveWebhook.mutate(url.trim());
                  }}
                  data-testid="integration-webhook-retry"
                >
                  {t('common.retry')}
                </Button>
              </div>
            ) : null}

            {handshakeCopy !== null ? (
              <div
                data-testid="integration-webhook-handshake-error"
                className={`flex flex-wrap items-center justify-between gap-2 rounded-md px-3 py-2 text-xs ${NOTICE_CLASS.warning}`}
              >
                <span>{handshakeCopy}</span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={!canSubmit}
                  onClick={() => {
                    saveWebhook.mutate(url.trim());
                  }}
                  data-testid="integration-webhook-handshake-retry"
                >
                  {t('common.retry')}
                </Button>
              </div>
            ) : null}
          </form>

          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span
              data-testid="integration-webhook-status"
              data-status={status}
              className={`rounded px-1.5 py-0.5 font-semibold ${WEBHOOK_STATUS_PILL[status]}`}
            >
              {t(`webhook.status.${status}`)}
            </span>
            {status === 'verified' && verifiedAt !== null ? (
              <span className="text-pw-text-muted">
                {t('webhook.verifiedAt', { when: verifiedAt })}
              </span>
            ) : null}
            {lastDeliveryAt !== null ? (
              <span className="text-pw-text-muted">
                {t('webhook.lastDeliveryAt', { when: lastDeliveryAt })}
              </span>
            ) : null}
            {data !== undefined && data.consecutive_failures > 0 ? (
              <span className="text-pw-text-muted">
                {t('webhook.consecutiveFailures', {
                  count: data.consecutive_failures,
                })}
              </span>
            ) : null}
          </div>

          {status === 'auto_disabled' ? (
            <p
              data-testid="integration-webhook-auto-disabled"
              className={`rounded-md px-3 py-2 text-xs ${NOTICE_CLASS.danger}`}
            >
              {t('webhook.autoDisabledHint')}
            </p>
          ) : status === 'unverified' ? (
            <p className={`rounded-md px-3 py-2 text-xs ${NOTICE_CLASS.warning}`}>
              {t('webhook.unverifiedHint')}
            </p>
          ) : null}

          {data?.secret_masked !== null && data?.secret_masked !== undefined ? (
            <div className="space-y-1">
              <span className="text-sm font-medium text-pw-text">
                {t('webhook.secretLabel')}
              </span>
              <div className="flex items-center gap-2">
                <Input
                  size="default"
                  readOnly
                  value={data.secret_masked}
                  aria-label={t('webhook.secretLabel')}
                  data-testid="integration-webhook-secret"
                  className="font-mono text-[13px]"
                />
                <Button
                  type="button"
                  variant="outline"
                  disabled={!role.canManage || rotateSecret.isPending}
                  onClick={() => {
                    setRotateOpen(true);
                  }}
                  data-testid="integration-webhook-rotate"
                >
                  {rotateSecret.isPending ? (
                    <Spinner size="sm" label={t('webhook.rotating')} />
                  ) : (
                    <IconRefresh />
                  )}
                  <span>
                    {rotateSecret.isPending ? t('webhook.rotating') : t('webhook.rotate')}
                  </span>
                </Button>
              </div>
              <p className="text-xs text-pw-text-muted">{t('webhook.rotateHelp')}</p>
              {data.rotation.in_window && windowExpiresAt !== null ? (
                <p
                  data-testid="integration-webhook-rotation-window"
                  className={`rounded-md px-3 py-2 text-xs ${NOTICE_CLASS.info}`}
                >
                  {t('webhook.rotationWindow', { when: windowExpiresAt })}
                </p>
              ) : null}
            </div>
          ) : null}

          {!role.canManage ? (
            <p className={`rounded-md px-3 py-2 text-xs ${NOTICE_CLASS.neutral}`}>
              {t('common.readOnlyHint')}
            </p>
          ) : null}
        </div>
      )}

      <ConfirmDialog
        open={rotateOpen}
        onOpenChange={(next) => {
          if (!next) setRotateOpen(false);
        }}
        type="warning"
        title={t('webhook.rotateConfirm.title')}
        description={t('webhook.rotateConfirm.description')}
        confirmLabel={t('webhook.rotateConfirm.confirm')}
        cancelLabel={t('common.cancel')}
        loading={rotateSecret.isPending}
        onConfirm={() => {
          rotateSecret.mutate();
        }}
      />

      <ShowOnceDialog
        open={rotatedSecret !== null}
        testId="integration-secret-rotated-modal"
        title={t('showOnce.secretTitle')}
        description={t('showOnce.secretDescription')}
        warning={t('showOnce.warningSecret')}
        value={rotatedSecret ?? ''}
        onClose={() => {
          setRotatedSecret(null);
        }}
        t={t}
      />
    </CardShell>
  );
}

// ---------------------------------------------------------------------------
// Card 3 — delivery log
// ---------------------------------------------------------------------------

const DELIVERY_STATUS_PILL: Record<DeliveryStatus, string> = {
  delivered: STATUS_PILL_CLASS.success,
  retrying: STATUS_PILL_CLASS.progress,
  failed: STATUS_PILL_CLASS.danger,
  suppressed: STATUS_PILL_CLASS.neutral,
};

const DELIVERY_PAGE_SIZE = 20;

function DeliveryLogCard({ role }: { role: RoleView }): JSX.Element {
  const { t } = useTranslation('integration');
  const qc = useQueryClient();

  const [status, setStatus] = useState<DeliveryStatus | ''>('');
  const [search, setSearch] = useState('');
  /** Keyset pagination: index 0 is page 1 (no cursor), each entry is the cursor that opens the NEXT page. */
  const [cursors, setCursors] = useState<string[]>([]);

  const cursor = cursors.length > 0 ? cursors[cursors.length - 1] : undefined;

  const deliveries: UseQueryResult<
    { rows: DeliveryRow[]; meta: DeliveriesMeta },
    IntegrationApiError
  > = useQuery({
    queryKey: [...DELIVERIES_QUERY_KEY, status, cursor ?? null],
    queryFn: async () => {
      const qs = new URLSearchParams();
      if (status !== '') qs.set('status', status);
      if (cursor !== undefined) qs.set('cursor', cursor);
      qs.set('limit', String(DELIVERY_PAGE_SIZE));
      const { data, meta } = await request<DeliveryRow[], DeliveriesMeta>(
        `/deliveries?${qs.toString()}`,
      );
      return {
        rows: data,
        meta: meta ?? { next_cursor: null, limit: DELIVERY_PAGE_SIZE },
      };
    },
    // P18.D-22 point 4 — 30s while the tab is open, plus the badge below.
    refetchInterval: REFETCH_MS,
    refetchIntervalInBackground: false,
  });

  const allRows = useMemo(() => deliveries.data?.rows ?? [], [deliveries.data]);

  // The contract's `ListDeliveriesQuery` has no free-text parameter (status /
  // cursor / limit only), so the search narrows the PAGE the server returned
  // rather than pretending to be a server-side query. Debounced anyway
  // (`SearchInput`, 350ms) so a fast typist does not re-render the table on
  // every keystroke, and the empty state says "no match for this filter"
  // instead of "nothing has ever been delivered".
  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (needle.length === 0) return allRows;
    return allRows.filter(
      (row) =>
        row.event_type.toLowerCase().includes(needle) ||
        row.event_id.toLowerCase().includes(needle),
    );
  }, [allRows, search]);

  const meta = deliveries.data?.meta;
  const hasNext = meta?.next_cursor != null;
  const page = cursors.length + 1;
  const isFiltered = status !== '' || search.trim().length > 0;

  const resetPaging = useCallback(() => {
    setCursors([]);
  }, []);

  return (
    <CardShell
      testId="integration-deliveries-card"
      title={t('deliveries.title')}
      description={t('deliveries.subtitle')}
      action={
        <FreshnessBadge
          testId="integration-deliveries-freshness"
          updatedAt={deliveries.dataUpdatedAt}
          isFetching={deliveries.isFetching}
          onRefresh={() => {
            void qc.invalidateQueries({ queryKey: DELIVERIES_QUERY_KEY });
          }}
          t={t}
        />
      }
    >
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <NativeSelect
          size="default"
          value={status}
          aria-label={t('deliveries.filterAria')}
          onChange={(event) => {
            const next = event.target.value;
            setStatus(
              (DELIVERY_STATUSES as readonly string[]).includes(next)
                ? (next as DeliveryStatus)
                : '',
            );
            resetPaging();
          }}
          data-testid="integration-deliveries-status-filter"
          className="w-48"
        >
          <option value="">{t('deliveries.filterAll')}</option>
          {DELIVERY_STATUSES.map((value) => (
            <option key={value} value={value}>
              {t(`deliveries.status.${value}`)}
            </option>
          ))}
        </NativeSelect>
        <div className="min-w-[240px] flex-1">
          <SearchInput
            value={search}
            onValueChange={setSearch}
            placeholder={t('deliveries.searchPlaceholder')}
            aria-label={t('deliveries.searchAria')}
            data-testid="integration-deliveries-search"
          />
        </div>
      </div>

      {deliveries.isLoading ? (
        <div className="flex items-center gap-2 py-8 text-sm text-pw-text-muted">
          <Spinner size="sm" label={t('common.loading')} />
          <span>{t('common.loading')}</span>
        </div>
      ) : deliveries.isError ? (
        <p
          data-testid="integration-deliveries-error"
          className={`rounded-md px-3 py-2 text-sm ${NOTICE_CLASS.danger}`}
        >
          {resolveErrorMessage(deliveries.error, t)}
        </p>
      ) : rows.length === 0 ? (
        <EmptyState
          testId="integration-deliveries-empty"
          title={
            isFiltered
              ? t('deliveries.empty.filteredTitle')
              : role.canManage
                ? t('deliveries.empty.ownerTitle')
                : t('deliveries.empty.staffTitle')
          }
          description={
            isFiltered
              ? t('deliveries.empty.filteredDescription')
              : role.canManage
                ? t('deliveries.empty.ownerDescription')
                : t('deliveries.empty.staffDescription')
          }
        />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-pw-border">
          <table className="w-full text-sm">
            <thead className="bg-pw-surface text-xs uppercase tracking-wider text-pw-text-muted">
              <tr>
                <th className="px-3 py-2 text-left">{t('deliveries.table.event')}</th>
                <th className="px-3 py-2 text-left">{t('deliveries.table.status')}</th>
                <th className="px-3 py-2 text-left">
                  {t('deliveries.table.attempts')}
                </th>
                <th className="px-3 py-2 text-left">
                  {t('deliveries.table.lastAttempt')}
                </th>
                <th className="px-3 py-2 text-left">
                  {t('deliveries.table.response')}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.delivery_id}
                  data-testid={`integration-delivery-row-${row.delivery_id}`}
                  className="border-t border-pw-border"
                >
                  {/* Event TYPE is a wire enum (`assignment.created`) — a value,
                      not copy, so it stays in its contract spelling. */}
                  <td className="px-3 py-2 font-mono text-[13px] text-pw-text">
                    {row.event_type}
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={`rounded px-1.5 py-0.5 text-xs font-semibold ${
                        DELIVERY_STATUS_PILL[row.status]
                      }`}
                    >
                      {t(`deliveries.status.${row.status}`)}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-pw-text-muted">
                    {t('deliveries.attempts', {
                      attempt: row.attempt,
                      max: row.max_attempts,
                    })}
                  </td>
                  <td className="px-3 py-2 text-pw-text-muted">
                    {formatDateTime(row.last_attempt_at) ?? '—'}
                  </td>
                  <td className="px-3 py-2">
                    {row.response_code === null ? (
                      <span className="text-pw-text-muted">
                        {t('deliveries.responseNone')}
                      </span>
                    ) : (
                      <span
                        className={`font-mono text-[13px] ${
                          row.response_code >= 200 && row.response_code < 300
                            ? 'text-green-700 dark:text-green-300'
                            : 'text-red-600 dark:text-red-300'
                        }`}
                      >
                        {row.response_code}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <footer className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-pw-text-muted">
        <span data-testid="integration-deliveries-showing">
          {`${t('deliveries.showing', { count: rows.length })} · ${t('deliveries.pageLabel', { page })}`}
        </span>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={cursors.length === 0}
            onClick={() => {
              setCursors((prev) => prev.slice(0, -1));
            }}
            data-testid="integration-deliveries-prev"
          >
            {t('deliveries.previous')}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!hasNext}
            onClick={() => {
              const next = meta?.next_cursor;
              if (next != null) setCursors((prev) => [...prev, next]);
            }}
            data-testid="integration-deliveries-next"
          >
            {t('deliveries.next')}
          </Button>
        </div>
      </footer>
    </CardShell>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

/**
 * "Your integration is switched off" — the sentence this page could not say.
 *
 * All three lane flags ship OFF, so a dark lane is the DEFAULT state of a first
 * deploy, not an edge case. Before this banner the page showed Active keys, a
 * Verified webhook and an enabled Create button while every call from the
 * vendor's production system was refused — leaving "our credentials are wrong"
 * or "their API is broken" as the only conclusions available to them.
 *
 * Renders NOTHING when the gate is absent (an older API that does not send the
 * fields) or when the lane is open. Silence is the right answer for "cannot
 * tell"; a wrong banner is worse than none.
 */
function LaneStatusBanner(): JSX.Element | null {
  const { t } = useTranslation('integration');
  const keys = useKeysQuery();
  const meta = keys.data?.meta;

  // `effective_enabled` is the AND of both switches. Absent => older API.
  if (meta?.effective_enabled !== false) return null;

  // `blocked_by` names the OUTER switch first: platform-wide beats per-vendor,
  // because the two sentences send the vendor to different places — one is
  // "wait, it is not you", the other is "ask your account manager".
  const reason = meta.blocked_by === 'vendor' ? 'vendor' : 'lane';

  return (
    <div
      role="status"
      data-testid="integration-lane-disabled"
      className={`rounded-md px-3 py-2 text-sm ${NOTICE_CLASS.warning}`}
    >
      <p className="font-semibold">{t(`laneStatus.${reason}.title`)}</p>
      <p className="mt-0.5">{t(`laneStatus.${reason}.body`)}</p>
    </div>
  );
}

export function IntegrationSettingsPage(): JSX.Element {
  const { t } = useTranslation('integration');
  // P18.D-01 — `VendorMembership.role` is the axis, and it has six values, not
  // two. `canManage` is owner-only; a multi-vendor user with no resolved vendor
  // lands on read-only, which is the safe default rather than a gap.
  const { canManage, vendorId, memberships } = useVendorStaffRole();

  const role: RoleView = {
    canManage,
    vendorId,
    isMultiVendor: memberships.length > 1,
  };

  return (
    <SettingsLayout
      portal="vendor"
      activeTab="integration"
      labels={{ tabs: { integration: t('nav.tab') }, nav: t('nav.aria') }}
    >
      <div className="space-y-5" data-testid="integration-settings-page">
        <PageHeader title={t('page.title')} subtitle={t('page.subtitle')} />

        <LaneStatusBanner />

        <IntegrationKeysCard role={role} />
        <WebhookCard role={role} />
        <DeliveryLogCard role={role} />
      </div>
    </SettingsLayout>
  );
}

export default IntegrationSettingsPage;
